import React from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { toast } from 'react-toastify';
import { SubSectionHeader } from '../shared/SubSectionHeader';
import { FormField } from '../shared/FormField';
import classes from '../../bidAnalysisTemplate.module.scss';

interface PricingCalculatorSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  formData: any;
  validationErrors: Record<string, string>;
  analysisType?: string;
  productType?: string; // Add productType to check if Direct or Indirect
  templateType?: string; // 'mas' | 'nc' - to distinguish between MAS and NC templates
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string, value: any) => void;
  onFieldKeyDown?: (field: string, e: React.KeyboardEvent) => void;
  onReplicate?: (field: string, value: any) => void;
}

export const PricingCalculatorSection: React.FC<PricingCalculatorSectionProps> = ({
  isExpanded,
  onToggle,
  formData,
  validationErrors,
  analysisType = 'New Launch',
  productType,
  templateType,
  onFieldChange,
  onFieldBlur,
  onFieldKeyDown,
  onReplicate,
}) => {
  const isPriceChange = analysisType === 'Price Change';
  const isDirect = productType === 'direct';
  // Check if this is NC template - handle both lowercase and uppercase, and normalize
  const normalizedTemplateType = templateType?.toLowerCase();
  const isNC = normalizedTemplateType === 'nc';
  return (
    <div className={classes.subSection}>
      <SubSectionHeader title="Pricing Calculator" isExpanded={isExpanded} onToggle={onToggle} />
      {isExpanded && (
        <div className={classes.subSectionContent}>
          <div className={classes.formGrid3}>
            {/* Hide TMS% fields for NC template */}
            {!isNC && (
              <>
                <FormField
                  label="VA TMS %"
                  required
                  error={validationErrors.vaTmsPercent}
                  helperText={onReplicate ? 'Replicate across pack size' : undefined}
                  onHelperClick={
                    onReplicate
                      ? () => onReplicate('vaTmsPercent', formData.vaTmsPercent)
                      : undefined
                  }
                >
                  <InputNumber
                    placeholder="Enter value"
                    value={(() => {
                      if (formData.vaTmsPercent == null || formData.vaTmsPercent === '')
                        return null;
                      const parsed =
                        typeof formData.vaTmsPercent === 'number'
                          ? formData.vaTmsPercent
                          : parseFloat(formData.vaTmsPercent);
                      return isNaN(parsed) ? null : parsed;
                    })()}
                    onValueChange={e => onFieldChange('vaTmsPercent', e.value)}
                    onBlur={e => onFieldBlur('vaTmsPercent', e)}
                    onKeyDown={e => onFieldKeyDown?.('vaTmsPercent', e)}
                    className={validationErrors.vaTmsPercent ? 'p-invalid' : ''}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </FormField>

                <FormField
                  label="DOD TMS %"
                  required
                  error={validationErrors.dodTmsPercent}
                  helperText={onReplicate ? 'Replicate across pack size' : undefined}
                  onHelperClick={
                    onReplicate
                      ? () => onReplicate('dodTmsPercent', formData.dodTmsPercent)
                      : undefined
                  }
                >
                  <InputNumber
                    placeholder="Enter value"
                    value={(() => {
                      if (formData.dodTmsPercent == null || formData.dodTmsPercent === '')
                        return null;
                      const parsed =
                        typeof formData.dodTmsPercent === 'number'
                          ? formData.dodTmsPercent
                          : parseFloat(formData.dodTmsPercent);
                      return isNaN(parsed) ? null : parsed;
                    })()}
                    onValueChange={e => onFieldChange('dodTmsPercent', e.value)}
                    onBlur={e => onFieldBlur('dodTmsPercent', e)}
                    onKeyDown={e => onFieldKeyDown?.('dodTmsPercent', e)}
                    className={validationErrors.dodTmsPercent ? 'p-invalid' : ''}
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </FormField>

                <FormField label="Total TMS %" required>
                  <InputNumber
                    value={(() => {
                      if (formData.totalTmsPercent == null || formData.totalTmsPercent === '')
                        return null;
                      const parsed =
                        typeof formData.totalTmsPercent === 'number'
                          ? formData.totalTmsPercent
                          : parseFloat(formData.totalTmsPercent);
                      return isNaN(parsed) ? null : parsed;
                    })()}
                    disabled
                    suffix="%"
                    mode="decimal"
                    minFractionDigits={2}
                    maxFractionDigits={2}
                  />
                </FormField>
              </>
            )}

            <FormField
              label="Suggested BID Price"
              required
              error={validationErrors.suggestedBidPrice}
            >
              <InputNumber
                value={(() => {
                  if (formData.suggestedBidPrice == null || formData.suggestedBidPrice === '')
                    return null;
                  const parsed =
                    typeof formData.suggestedBidPrice === 'number'
                      ? formData.suggestedBidPrice
                      : parseFloat(formData.suggestedBidPrice);
                  return isNaN(parsed) ? null : parsed;
                })()}
                className={`${classes.redText} ${validationErrors.suggestedBidPrice ? 'p-invalid' : ''}`}
                onValueChange={e => {
                  // Always ensure 2 decimal precision
                  if (e.value !== null && e.value !== undefined) {
                    const roundedValue = Math.round(e.value * 100) / 100;
                    onFieldChange('suggestedBidPrice', roundedValue);
                  } else {
                    onFieldChange('suggestedBidPrice', e.value);
                  }
                }}
                onBlur={e => {
                  // Ensure value has 2 decimal places on blur
                  if (
                    formData.suggestedBidPrice !== null &&
                    formData.suggestedBidPrice !== undefined &&
                    formData.suggestedBidPrice !== ''
                  ) {
                    const numValue =
                      typeof formData.suggestedBidPrice === 'number'
                        ? formData.suggestedBidPrice
                        : parseFloat(formData.suggestedBidPrice);
                    if (!isNaN(numValue)) {
                      const roundedValue = Math.round(numValue * 100) / 100;
                      onFieldChange('suggestedBidPrice', roundedValue);
                    }
                  }
                  onFieldBlur('suggestedBidPrice', e);
                }}
                onKeyDown={e => onFieldKeyDown?.('suggestedBidPrice', e)}
                placeholder="$"
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
                prefix="$"
              />
            </FormField>

            <FormField label="WAC">
              <InputNumber
                value={(() => {
                  if (formData.wac == null || formData.wac === '') return null;
                  const parsed =
                    typeof formData.wac === 'number' ? formData.wac : parseFloat(formData.wac);
                  return isNaN(parsed) ? null : parsed;
                })()}
                onValueChange={e => onFieldChange('wac', e.value?.toString() || '0')}
                onBlur={e => onFieldBlur('wac', e)}
                onKeyDown={e => onFieldKeyDown?.('wac', e)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                min={0}
                placeholder="Enter WAC"
              />
            </FormField>

            {/* Supplier Comparable WAC - For Direct products and NC template */}
            {(isDirect || isNC) && (
              <FormField label="Supplier Comparable WAC">
                <InputNumber
                  value={(() => {
                    const value = formData.supplier_comparable_wac;
                    if (value == null || value === '' || value === undefined) {
                      return null;
                    }
                    if (typeof value === 'number') {
                      return isNaN(value) ? null : value;
                    }
                    const parsed = parseFloat(value);
                    return isNaN(parsed) ? null : parsed;
                  })()}
                  onValueChange={e =>
                    onFieldChange('supplier_comparable_wac', e.value?.toString() || '0')
                  }
                  onBlur={e => onFieldBlur('supplier_comparable_wac', e)}
                  onKeyDown={e => onFieldKeyDown?.('supplier_comparable_wac', e)}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  min={0}
                  placeholder="Enter Supplier Comparable WAC"
                />
              </FormField>
            )}

            <FormField
              label="GSMS Profit"
              required
              error={validationErrors.gsmsProfit}
              helperText={onReplicate ? 'Replicate across pack size' : undefined}
              onHelperClick={
                onReplicate
                  ? () => {
                      // Check if suggested bid price exists before allowing replication
                      const suggestedBidPrice = parseFloat(formData.suggestedBidPrice || '0');
                      if (suggestedBidPrice <= 0) {
                        toast.error(
                          'Cannot replicate GSMS Profit: Suggested BID Price is required. Please enter a Suggested BID Price first.',
                          { autoClose: 5000 }
                        );
                        return;
                      }
                      onReplicate('gsmsProfit', formData.gsmsProfit);
                    }
                  : undefined
              }
            >
              <InputNumber
                value={(() => {
                  if (formData.gsmsProfit == null || formData.gsmsProfit === '') {
                    return isPriceChange ? null : 0;
                  }
                  const parsed =
                    typeof formData.gsmsProfit === 'number'
                      ? formData.gsmsProfit
                      : parseFloat(formData.gsmsProfit);
                  return isNaN(parsed) ? (isPriceChange ? null : 0) : parsed;
                })()}
                onValueChange={e => {
                  // Check if suggested bid price exists before allowing input
                  const suggestedBidPrice = parseFloat(formData.suggestedBidPrice || '0');
                  if (suggestedBidPrice <= 0) {
                    toast.error(
                      'Cannot input GSMS Profit: Suggested BID Price is required. Please enter a Suggested BID Price first.',
                      { autoClose: 5000 }
                    );
                    return;
                  }

                  // Allow negative values for all analysis types (cost can be higher than price)
                  onFieldChange('gsmsProfit', e.value);
                }}
                onBlur={e => onFieldBlur('gsmsProfit', e)}
                onKeyDown={e => onFieldKeyDown?.('gsmsProfit', e)}
                className={validationErrors.gsmsProfit ? 'p-invalid' : ''}
                suffix="%"
                mode="decimal"
                minFractionDigits={2}
                maxFractionDigits={2}
                min={-100}
                max={100}
                // Always editable - negative values allowed (cost > price scenario)
              />
            </FormField>
          </div>
        </div>
      )}
    </div>
  );
};
